// Utilities
import { collection, deleteField, getDoc, getDocs, query, where, doc, addDoc, updateDoc, serverTimestamp, Timestamp, type UpdateData, type DocumentData } from 'firebase/firestore';
import { defineStore } from 'pinia';
import { useFirestore } from 'vuefire';
import { Project, ProjectCreationRequest, type TracexSyncConfig } from '@/models/Project';
import { useUsersStore } from './users';
import { useTenantsStore } from './tenants';
import { logger } from '@/utils/logger'
import { useAppStore } from './app'
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import i18n from '@/plugins/i18n'
import { CronExpressionParser } from 'cron-parser'
import type { DocumentReference } from 'firebase/firestore'


export const useProjectsStore = defineStore('projects', {
  state: () => ({
    loadedOnce: false,
    /** The last list read failed (MYAC-1522): the dashboard says so instead of staying blank. */
    loadFailed: false,
    currentProjectId: "",
    projects: [] as Project[],
    projectCreationRequestSnapshot: null as any,
    _projectCreationRequestUnsub: null as any,
  }),
  getters: {
    currentProject(state): Project | undefined {
      const project = state.projects.find((p) => p.id === state.currentProjectId)
      if (!project) return undefined
      const tenantsStore = useTenantsStore()
      if (tenantsStore.currentTenantId && project.tenant?.id && project.tenant.id !== tenantsStore.currentTenantId) {
        return undefined
      }
      return project
    },
    /**
     * Projects filtered to the active tenant. UI consumers should prefer this
     * over the raw `projects` array to avoid leaking cross-tenant data into
     * menus, dashboards, quotas, etc. Projects with no resolved tenant id are
     * intentionally excluded so we never accidentally display them.
     */
    tenantProjects(state): Project[] {
      const tenantsStore = useTenantsStore()
      const currentTenantId = tenantsStore.currentTenantId
      if (!currentTenantId) return []
      return state.projects.filter((p) => p.tenant?.id === currentTenantId)
    },
    projectCreationRequest: (state) => state.projectCreationRequestSnapshot ? ProjectCreationRequest.fromFirebase(state.projectCreationRequestSnapshot) : null,
  },
  actions: {
    /**
     * One read of a project's own doc, for a route whose project the loaded list
     * does not hold (yet): true when it exists. Throws what the read throws — a
     * permission-denied is how the router guard tells « no access » apart.
     */
    async projectExists(pid: string): Promise<boolean> {
      const snap = await getDoc(doc(useFirestore(), 'projects', pid))
      return snap.exists()
    },
    setCurrentProject(id: string) {
      this.currentProjectId = id;
    },
    listProjects: async function (forceUpdate = false) {
      try {
        logger.debug("listProjects", forceUpdate)
        if (this.loadedOnce && !forceUpdate) return Promise.resolve();
        const uid = useUsersStore().uid;
        if (!uid) {
          logger.error("No user uid to list projects")
          return
        }
        logger.debug("Listing projects for user", uid)
        const q = query(collection(useFirestore(), "projects"), where("members", 'array-contains', uid));
        const r = await getDocs(q);
        this.projects = r.docs.map((p) => Project.fromFirebase(p)).filter((p): p is Project => p !== null && p.status !== 'soft_deleted');
        // No default project (MYAC-1348): currentProjectId is the project of the
        // page on screen, set by the project route guards and cleared elsewhere.
        this.loadedOnce = true;
        this.loadFailed = false;
      } catch (error) {
        logger.error("Error getting projects", error);
        this.loadFailed = true;
      }
    },
    async pollUntilHasAny({ timeoutMs = 12000, intervalMs = 750 }: { timeoutMs?: number; intervalMs?: number } = {}): Promise<void> {
      const start = Date.now();
      while (Date.now() - start < timeoutMs) {
        try {
          await this.listProjects(true);
        } catch { }
        if (this.projects && this.projects.length > 0) return;
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }
    },
    // List all projects belonging to a tenant (for tenant admins to see all projects when inviting users)
    async listProjectsByTenant(tenantId: string): Promise<Project[]> {
      return (await this.readProjectsByTenant(tenantId)) ?? []
    },

    /** The same read, with a failure told apart from « none » (null): MYAC-1522, the empty state's admin read. */
    async readProjectsByTenant(tenantId: string): Promise<Project[] | null> {
      try {
        if (!tenantId) {
          logger.error("listProjectsByTenant: tenantId is required")
          return []
        }
        const db = useFirestore()
        const tenantRef = doc(db, "tenants", tenantId)
        const q = query(collection(db, "projects"), where("tenant", '==', tenantRef))
        const r = await getDocs(q)
        return r.docs.map((p) => Project.fromFirebase(p)).filter((p): p is Project => p !== null && p.status !== 'soft_deleted')
      } catch (error) {
        logger.error("Error listing projects by tenant", error)
        return null
      }
    },

    async deleteProject(project: Project): Promise<void> {
      const uid = useUsersStore().uid
      if (!project.id || !uid) {
        logger.error('deleteProject: missing project id or uid')
        return
      }
      try {
        const firestore = useFirestore()
        const ref = collection(firestore, `projects/${project.id}/command_queues/delete_project/commands`)
        await addDoc(ref, {
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          status: 'requested',
          requested_by: uid,
          payload: { kind: 'delete_project' },
        })
        this.projects = this.projects.filter((p) => p.id !== project.id)
        if (this.currentProjectId === project.id) this.currentProjectId = ''
        useAppStore().toast(
          i18n.global.t('projects.delete.success_title'),
          i18n.global.t('projects.delete.success'),
          'success',
        )
      } catch (error) {
        logger.error('deleteProject failed', error)
        useAppStore().toast(
          i18n.global.t('projects.delete.error_title'),
          i18n.global.t('projects.delete.error'),
          'error',
        )
        throw error
      }
    },
    async updateAssessmentScheduling(
      projectId: string,
      scheduling: {
        enabled: boolean
        cron: string | null
        available_standard_ref: DocumentReference | null
      }
    ): Promise<void> {
      try {
        if (!projectId) {
          logger.error('updateAssessmentScheduling: projectId is required')
          return
        }

        const db = useFirestore()
        const projectRef = doc(db, 'projects', projectId)

        const payload: UpdateData<DocumentData> = {
          'assessment_scheduling.enabled': scheduling.enabled,
          'assessment_scheduling.cron': scheduling.cron,
          'assessment_scheduling.available_standard_ref': scheduling.available_standard_ref,
          updated_at: serverTimestamp(),
        }

        // Always recalculate next_run when enabling and cron is set (fixes date not updating on frequency change)
        if (scheduling.enabled && scheduling.cron) {
          try {
            const interval = CronExpressionParser.parse(scheduling.cron, { tz: 'UTC' })
            const nextDate = interval.next().toDate()
            payload['assessment_scheduling.next_run'] = Timestamp.fromDate(nextDate)
          } catch (error) {
            logger.error('Failed to parse cron expression', scheduling.cron, error)
            useAppStore().toast(
              i18n.global.t('assessments.scheduling.save_error'),
              i18n.global.t('assessments.scheduling.invalid_cron'),
              'error',
              4000,
            )
            return
          }
        } else if (!scheduling.enabled) {
          // Clear next_run when disabling
          payload['assessment_scheduling.next_run'] = null
        }

        await updateDoc(projectRef, payload)
        if (scheduling.enabled) { try { trackAction(CsmAction.AssessmentScheduled, { projectId }) } catch { } }

        // Refresh projects list to get updated data
        await this.listProjects(true)

        useAppStore().toast(
          i18n.global.t('assessments.scheduling.save_success'),
          '',
          'success',
          3000,
        )
      } catch (error) {
        logger.error('Error updating assessment scheduling', error)
        useAppStore().toast(
          i18n.global.t('assessments.scheduling.save_error'),
          (error as Error)?.message || '',
          'error',
          4000,
        )
      }
    },
    async updateTDScheduling(
      projectId: string,
      scheduling: {
        enabled: boolean
        cron: string | null
      }
    ): Promise<void> {
      try {
        if (!projectId) {
          logger.error('updateTDScheduling: projectId is required')
          return
        }

        const db = useFirestore()
        const projectRef = doc(db, 'projects', projectId)

        const payload: UpdateData<DocumentData> = {
          'td_scheduling.enabled': scheduling.enabled,
          'td_scheduling.cron': scheduling.cron,
          updated_at: serverTimestamp(),
        }

        // Always recalculate next_run when enabling and cron is set (fixes date not updating on frequency change)
        if (scheduling.enabled && scheduling.cron) {
          try {
            const interval = CronExpressionParser.parse(scheduling.cron, { tz: 'UTC' })
            const nextDate = interval.next().toDate()
            payload['td_scheduling.next_run'] = Timestamp.fromDate(nextDate)
          } catch (error) {
            logger.error('Failed to parse cron expression', scheduling.cron, error)
            useAppStore().toast(
              i18n.global.t('td.scheduling.save_error'),
              i18n.global.t('td.scheduling.invalid_cron'),
              'error',
              4000,
            )
            return
          }
        } else if (!scheduling.enabled) {
          // Clear next_run when disabling
          payload['td_scheduling.next_run'] = null
        }

        await updateDoc(projectRef, payload)
        if (scheduling.enabled) { try { trackAction(CsmAction.TdScheduled, { projectId }) } catch { } }

        // Refresh projects list to get updated data
        await this.listProjects(true)

        useAppStore().toast(
          i18n.global.t('td.scheduling.save_success'),
          '',
          'success',
          3000,
        )
      } catch (error) {
        logger.error('Error updating TD scheduling', error)
        useAppStore().toast(
          i18n.global.t('td.scheduling.save_error'),
          (error as Error)?.message || '',
          'error',
          4000,
        )
      }
    },
    async updateRegulationWatchScheduling(
      projectId: string,
      scheduling: {
        enabled: boolean
        cron: string | null
        lookback_days: number | null
        /** MYAC-1308 — the schedule also runs the suggestions for every product. */
        suggestions_enabled: boolean
      }
    ): Promise<void> {
      try {
        if (!projectId) {
          logger.error('updateRegulationWatchScheduling: projectId is required')
          return
        }

        const db = useFirestore()
        const projectRef = doc(db, 'projects', projectId)

        const payload: UpdateData<DocumentData> = {
          'regulation_watch_scheduling.enabled': scheduling.enabled,
          'regulation_watch_scheduling.cron': scheduling.cron,
          'regulation_watch_scheduling.lookback_days': scheduling.lookback_days,
          // The preference as ticked, kept while the schedule is off; the
          // backend runs the suggestions only when both are on.
          'regulation_watch_scheduling.suggestions_enabled': scheduling.suggestions_enabled,
          updated_at: serverTimestamp(),
        }

        if (scheduling.enabled && scheduling.cron) {
          try {
            const interval = CronExpressionParser.parse(scheduling.cron, { tz: 'UTC' })
            const nextDate = interval.next().toDate()
            payload['regulation_watch_scheduling.next_run'] = Timestamp.fromDate(nextDate)
            // MYAC-1308 — the suggestion half has its own due date (the backend
            // advances each half on its own), set to the same tick as the report's.
            payload['regulation_watch_scheduling.suggestions_next_run'] = scheduling.suggestions_enabled
              ? Timestamp.fromDate(nextDate)
              : null
          } catch (error) {
            logger.error('Failed to parse cron expression', scheduling.cron, error)
            useAppStore().toast(
              i18n.global.t('regulatory_monitoring.scheduling.save_error'),
              i18n.global.t('regulatory_monitoring.scheduling.invalid_cron'),
              'error',
              4000,
            )
            return
          }
        } else if (!scheduling.enabled) {
          payload['regulation_watch_scheduling.next_run'] = null
          payload['regulation_watch_scheduling.suggestions_next_run'] = null
        }

        await updateDoc(projectRef, payload)
        if (scheduling.enabled) { try { trackAction(CsmAction.RegulationWatchConfigured, { projectId }) } catch { } }

        await this.listProjects(true)

        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.scheduling.save_success'),
          '',
          'success',
          3000,
        )
      } catch (error) {
        logger.error('Error updating regulation watch scheduling', error)
        useAppStore().toast(
          i18n.global.t('regulatory_monitoring.scheduling.save_error'),
          (error as Error)?.message || '',
          'error',
          4000,
        )
      }
    },
    /**
     * Crée ou met à jour le lien TraceX pour le projet courant.
     * Le `project_ref` est opaque côté front — on le passe tel quel à Firestore.
     */
    async updateTracexSyncConfig(
      projectId: string,
      config: Omit<TracexSyncConfig, 'configured_at' | 'configured_by_uid'> & {
        configured_by_uid?: string | null
      }
    ): Promise<void> {
      try {
        if (!projectId) {
          logger.error('updateTracexSyncConfig: projectId is required')
          return
        }
        const db = useFirestore()
        const projectRef = doc(db, 'projects', projectId)
        const payload: UpdateData<DocumentData> = {
          'tracex_sync_config.datasource_id': config.datasource_id,
          'tracex_sync_config.project_ref': config.project_ref,
          'tracex_sync_config.project_identifier': config.project_identifier,
          'tracex_sync_config.project_name': config.project_name,
          'tracex_sync_config.auto_export_on_accept': config.auto_export_on_accept,
          'tracex_sync_config.configured_at': serverTimestamp(),
          'tracex_sync_config.configured_by_uid': config.configured_by_uid ?? null,
          updated_at: serverTimestamp(),
        }
        await updateDoc(projectRef, payload)
        await this.listProjects(true)
        useAppStore().toast(
          i18n.global.t('tasks.tracex_sync.config_saved'),
          '',
          'success',
          3000,
        )
      } catch (error) {
        logger.error('Error updating tracex sync config', error)
        useAppStore().toast(
          i18n.global.t('tasks.tracex_sync.config_save_error'),
          (error as Error)?.message || '',
          'error',
          4000,
        )
      }
    },
    /**
     * Désactive la synchro TraceX en supprimant complètement le bloc
     * `tracex_sync_config` du projet.
     */
    async clearTracexSyncConfig(projectId: string): Promise<void> {
      try {
        if (!projectId) {
          logger.error('clearTracexSyncConfig: projectId is required')
          return
        }
        const db = useFirestore()
        const projectRef = doc(db, 'projects', projectId)
        await updateDoc(projectRef, {
          tracex_sync_config: deleteField(),
          updated_at: serverTimestamp(),
        })
        await this.listProjects(true)
        useAppStore().toast(
          i18n.global.t('tasks.tracex_sync.config_cleared'),
          '',
          'success',
          3000,
        )
      } catch (error) {
        logger.error('Error clearing tracex sync config', error)
        useAppStore().toast(
          i18n.global.t('tasks.tracex_sync.config_save_error'),
          (error as Error)?.message || '',
          'error',
          4000,
        )
      }
    },
    async updateTDSettings(
      projectId: string,
      settings: {
        market_placement_date?: Date | null
      }
    ): Promise<void> {
      try {
        if (!projectId) {
          logger.error('updateTDSettings: projectId is required')
          return
        }

        const db = useFirestore()
        const projectRef = doc(db, 'projects', projectId)

        const payload: UpdateData<DocumentData> = {
          updated_at: serverTimestamp(),
        }

        if ('market_placement_date' in settings) {
          payload['market_placement_date'] = settings.market_placement_date
            ? Timestamp.fromDate(settings.market_placement_date)
            : null
        }

        await updateDoc(projectRef, payload)
        await this.listProjects(true)
      } catch (error) {
        logger.error('updateTDSettings failed', error)
        throw error
      }
    },
  }
})
